// Typed fetch client for the api.* sibling. Every call takes an
// `authHeaders` thunk (see lib/auth.ts) so the freshest Clerk token is
// attached per request. A 401 surfaces as `Unauthorized` so callers can
// bounce to sign-in.

import { apiUrl } from "./urls";
import { signupIntentHeader } from "./intent";
import { analyticsConsentHeader } from "../analytics";
import type { AuthHeaders } from "./auth";
import type {
  ActivitySession,
  Billing,
  Breakdown,
  BudgetInput,
  AuditPage,
  Chargeback,
  BudgetsOverview,
  InsightDimension,
  InsightFilters,
  OrgTeam,
  RaiseRequest,
  SeriesMetric,
  SpendBudget,
  TimelineRequest,
  ToolDimension,
  ToolsBreakdown,
  UseCaseAxis,
  UseCases,
  Flow,
  FlowBy,
  FlowMeasure,
  UsageValue,
  CreditsActivity,
  BudgetUpdate,
  DailyPoint,
  DailySpend,
  DateRange,
  KeysUsage,
  Me,
  OrgBilling,
  OrgCredits,
  OrgDevices,
  OrgInvitation,
  OrgKey,
  OrgKeyMinted,
  OrgWebhook,
  OrgWebhookMinted,
  WebhookDelivery,
  KeyAccess,
  OrgLeaderboard,
  OrgInvoice,
  OrgInvoiceList,
  OrgMember,
  OrgModelUsage,
  OrgModelUsageList,
  OrgModels,
  OrgOverview,
  OrgRateLimits,
  OrgRequestLogPage,
  RequestLogFilters,
  OrgSettings,
  OrgTraffic,
  OrgUsage,
  SeatTier,
  SessionsOut,
  SpendCapUpdate,
  UsageSeries,
  UsageGroupBy,
} from "./types";

export class Unauthorized extends Error {}

// A seat toggle was rejected because the org has no free seat of that tier.
export class NoCapacity extends Error {}

async function authedFetch(
  path: string,
  authHeaders: AuthHeaders,
  init: RequestInit = {},
): Promise<Response> {
  const send = async (fresh?: boolean) => {
    const headers: HeadersInit = {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...signupIntentHeader(),
      ...analyticsConsentHeader(),
      ...(await (fresh ? authHeaders(true) : authHeaders())),
      ...(init.headers || {}),
    };
    return fetch(`${apiUrl()}${path}`, { ...init, headers }).catch((e) => {
      console.error(path, e);
      throw new Error(
        "Couldn't reach condense. Check your connection and try again.",
      );
    });
  };
  const r = await send();
  // A cached token can expire in flight; only a new one being refused ends a session.
  return r.status === 401 ? send(true) : r;
}

// A server detail that is a sentence reaches the customer; codes, 5xx bodies
// and validation lists stay in the console. ``action`` completes "Couldn't …".
async function okOrThrow(r: Response, action: string): Promise<void> {
  if (r.ok) return;
  const body = await r.json().catch(() => null);
  console.error(`${action}: ${r.status}`, body);
  const detail = body?.detail;
  if (r.status < 500 && typeof detail === "string" && detail.includes(" "))
    throw new Error(detail[0].toUpperCase() + detail.slice(1));
  if (r.status === 401)
    throw new Error("Your session has expired. Sign in again.");
  if (r.status === 403) throw new Error("You don't have access to this.");
  if (r.status === 429)
    throw new Error("Too many requests. Wait a minute and try again.");
  throw new Error(`Couldn't ${action}. Try again in a moment.`);
}

async function jsonOrThrow<T>(r: Response, action: string): Promise<T> {
  await okOrThrow(r, action);
  return (await r.json()) as T;
}

export async function getMe(authHeaders: AuthHeaders): Promise<Me> {
  const r = await authedFetch("/v1/me", authHeaders);
  if (r.status === 401) throw new Unauthorized();
  return jsonOrThrow(r, "load your account");
}

export async function getBilling(authHeaders: AuthHeaders): Promise<Billing> {
  const r = await authedFetch("/v1/me/billing", authHeaders);
  if (r.status === 401) throw new Unauthorized();
  return jsonOrThrow(r, "load your billing");
}

export async function openBillingPortal(
  authHeaders: AuthHeaders,
  flow?: "cancel" | "update_payment",
): Promise<string> {
  const r = await authedFetch("/v1/me/billing/portal", authHeaders, {
    method: "POST",
    body: JSON.stringify({ flow: flow ?? null }),
  });
  if (r.status === 401) throw new Unauthorized();
  await okOrThrow(r, "open the billing portal");
  return ((await r.json()) as { url: string }).url;
}

export async function startBillingCheckout(
  authHeaders: AuthHeaders,
  tier: string,
): Promise<string> {
  const r = await authedFetch(
    `/v1/me/billing/checkout?tier=${encodeURIComponent(tier)}`,
    authHeaders,
    { method: "POST" },
  );
  if (r.status === 401) throw new Unauthorized();
  await okOrThrow(r, "start checkout");
  return ((await r.json()) as { url: string }).url;
}

export async function postSignupIntent(
  authHeaders: AuthHeaders,
  intent: "org" | "single",
): Promise<void> {
  await authedFetch("/v1/me/signup-intent", authHeaders, {
    method: "POST",
    body: JSON.stringify({ intent }),
  });
}

export async function setToggle(
  authHeaders: AuthHeaders,
  key: string,
  value: boolean,
): Promise<void> {
  const r = await authedFetch(
    `/v1/me/toggles/${encodeURIComponent(key)}`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ value: value ? "on" : "off" }) },
  );
  await okOrThrow(r, "change this setting");
}

export async function ackOnboarding(
  authHeaders: AuthHeaders,
  key: string,
): Promise<void> {
  const r = await authedFetch(
    `/v1/me/onboarding/${encodeURIComponent(key)}`,
    authHeaders,
    { method: "POST" },
  );
  await okOrThrow(r, "save your progress");
}

export async function mintToken(
  authHeaders: AuthHeaders,
  name: string | null,
): Promise<string> {
  const r = await authedFetch("/v1/tokens", authHeaders, {
    method: "POST",
    body: JSON.stringify({ name: name || null }),
  });
  await okOrThrow(r, "create the key");
  const body = (await r.json()) as { secret: string };
  return body.secret;
}

export async function revokeToken(
  authHeaders: AuthHeaders,
  id: string,
): Promise<void> {
  const r = await authedFetch(
    `/v1/tokens/${encodeURIComponent(id)}/revoke`,
    authHeaders,
    { method: "POST" },
  );
  await okOrThrow(r, "revoke the key");
}

// Returns the raw Response so the /cli page can branch on 404 vs other.
export async function deviceLink(
  authHeaders: AuthHeaders,
  userCode: string,
): Promise<Response> {
  return authedFetch("/v1/device/link", authHeaders, {
    method: "POST",
    body: JSON.stringify({ user_code: userCode }),
  });
}

export async function getDailySavings(
  authHeaders: AuthHeaders,
  days = 30,
): Promise<DailyPoint[]> {
  const r = await authedFetch(`/v1/me/savings/daily?days=${days}`, authHeaders);
  await okOrThrow(r, "load your savings");
  const body = (await r.json()) as { days: DailyPoint[] };
  return body.days || [];
}

const EMPTY_USAGE: UsageSeries = {
  group_by: "intercept",
  groups: [],
  labels: {},
  totals: {
    pre_tokens: 0,
    post_tokens: 0,
    tokens_saved: 0,
    requests: 0,
    cache_read: 0,
    cache_write: 0,
    money_saved: "0",
    compression_pct: null,
    avg_added_ms: 0,
  },
  buckets: [],
};

export async function getUsageSeries(
  authHeaders: AuthHeaders,
  window: string | DateRange,
  groupBy: UsageGroupBy = "intercept",
  orgId?: string,
  keyId?: string,
): Promise<UsageSeries> {
  const base = orgId ? `/v1/orgs/${orgId}/usage/series` : `/v1/me/usage/series`;
  const key = keyId ? `&key_id=${encodeURIComponent(keyId)}` : "";
  const when =
    typeof window === "string"
      ? `month=${window}`
      : `start=${window.start}&end=${window.end}`;
  const r = await authedFetch(
    `${base}?${when}&group_by=${groupBy}${key}`,
    authHeaders,
  );
  if (!r.ok) return EMPTY_USAGE;
  return (await r.json()) as UsageSeries;
}

// Per-key traffic over a trailing window, for the API keys table.
export async function getKeysUsage(
  authHeaders: AuthHeaders,
  days = 30,
): Promise<KeysUsage> {
  const r = await authedFetch(`/v1/me/keys/usage?days=${days}`, authHeaders);
  if (!r.ok) return { days, keys: [] };
  return (await r.json()) as KeysUsage;
}

// Analytics: org usage series over an arbitrary [start, end] day range.
export async function getOrgUsageRange(
  authHeaders: AuthHeaders,
  orgId: string,
  range: DateRange,
  groupBy: UsageGroupBy = "intercept",
): Promise<UsageSeries> {
  const q = `start=${range.start}&end=${range.end}&group_by=${groupBy}`;
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/usage/series?${q}`,
    authHeaders,
  );
  if (!r.ok) return EMPTY_USAGE;
  return (await r.json()) as UsageSeries;
}

export async function getOrgUsageByModel(
  authHeaders: AuthHeaders,
  orgId: string,
  range: DateRange,
): Promise<OrgModelUsage[]> {
  const q = `start=${range.start}&end=${range.end}`;
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/usage/by-model?${q}`,
    authHeaders,
  );
  await okOrThrow(r, "load usage by model");
  return ((await r.json()) as OrgModelUsageList).models;
}

export async function getOrgRequests(
  authHeaders: AuthHeaders,
  orgId: string,
  range: DateRange,
  page: number,
  q: string,
  filters: RequestLogFilters = {},
): Promise<OrgRequestLogPage> {
  const params = requestLogParams(range, q, filters);
  params.set("page", String(page));
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/requests?${params.toString()}`,
    authHeaders,
  );
  return jsonOrThrow(r, "load requests");
}

function requestLogParams(
  range: DateRange,
  q: string,
  filters: RequestLogFilters,
): URLSearchParams {
  const params = new URLSearchParams({ start: range.start, end: range.end });
  if (q) params.set("q", q);
  for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
  return params;
}

export async function downloadOrgRequests(
  authHeaders: AuthHeaders,
  orgId: string,
  range: DateRange,
  q: string,
  filters: RequestLogFilters = {},
): Promise<void> {
  const params = requestLogParams(range, q, filters);
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/requests/export?${params.toString()}`,
    authHeaders,
  );
  await okOrThrow(r, "download the CSV");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(await r.blob());
  a.download = `condense-requests-${range.start}-${range.end}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function getOrgRateLimits(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgRateLimits> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/rate-limits`,
    authHeaders,
  );
  return jsonOrThrow(r, "load rate limits");
}

export async function getOrgInvoices(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgInvoice[]> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/invoices`,
    authHeaders,
  );
  await okOrThrow(r, "load invoices");
  return ((await r.json()) as OrgInvoiceList).invoices;
}

export async function getOrgCreditsDaily(
  authHeaders: AuthHeaders,
  orgId: string,
  range: DateRange,
): Promise<DailySpend> {
  const q = `start=${range.start}&end=${range.end}`;
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/credits/daily?${q}`,
    authHeaders,
  );
  return jsonOrThrow(r, "load credit usage");
}

export async function getSessions(
  authHeaders: AuthHeaders,
): Promise<SessionsOut> {
  const r = await authedFetch("/v1/me/sessions", authHeaders);
  return jsonOrThrow(r, "load sessions");
}

export async function getOrgMembers(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgMember[]> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/members`,
    authHeaders,
  );
  return jsonOrThrow(r, "load members");
}

export async function getOrgUsage(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgUsage> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/usage`,
    authHeaders,
  );
  return jsonOrThrow(r, "load usage");
}

export async function getOrgLeaderboard(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgLeaderboard> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/leaderboard`,
    authHeaders,
  );
  return jsonOrThrow(r, "load the leaderboard");
}

export async function getOrgCredits(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgCredits> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/credits`,
    authHeaders,
  );
  return jsonOrThrow(r, "load credits");
}

export async function setMemberBudget(
  authHeaders: AuthHeaders,
  orgId: string,
  userId: string,
  body: BudgetUpdate,
): Promise<OrgMember> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/budget`,
    authHeaders,
    { method: "POST", body: JSON.stringify(body) },
  );
  return jsonOrThrow(r, "save the limit");
}

// Assign (or release, tier=null) a member's paid seat tier. 409 means the
// org has no free seat of that tier — surface it so the caller can prompt a
// capacity purchase in Manage seats.
export async function setMemberSeat(
  authHeaders: AuthHeaders,
  orgId: string,
  userId: string,
  tier: SeatTier | null,
): Promise<OrgMember> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/seat`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ tier }) },
  );
  if (r.status === 409) throw new NoCapacity();
  return jsonOrThrow(r, "change the seat");
}

export async function getOrgDevices(
  authHeaders: AuthHeaders,
  orgId: string,
  days: number,
): Promise<OrgDevices> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/devices?days=${days}`,
    authHeaders,
  );
  return jsonOrThrow(r, "load devices");
}

export async function setMemberDense(
  authHeaders: AuthHeaders,
  orgId: string,
  userId: string,
  enabled: boolean,
): Promise<{ user_id: string; dense_enabled: boolean }> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/dense`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ enabled }) },
  );
  await okOrThrow(r, "change dense for this member");
  return (await r.json()) as { user_id: string; dense_enabled: boolean };
}

export async function setDeviceDense(
  authHeaders: AuthHeaders,
  orgId: string,
  deviceId: string,
  enabled: boolean,
): Promise<{ device_id: string; dense_enabled: boolean }> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/devices/${encodeURIComponent(deviceId)}/dense`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ enabled }) },
  );
  await okOrThrow(r, "change dense for this device");
  return (await r.json()) as { device_id: string; dense_enabled: boolean };
}

export async function removeOrgDevice(
  authHeaders: AuthHeaders,
  orgId: string,
  deviceId: string,
): Promise<{ device_id: string; status: string }> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/devices/${encodeURIComponent(deviceId)}/remove`,
    authHeaders,
    { method: "POST" },
  );
  await okOrThrow(r, "remove the device");
  return (await r.json()) as { device_id: string; status: string };
}

export async function setMemberRole(
  authHeaders: AuthHeaders,
  orgId: string,
  userId: string,
  role: string,
): Promise<OrgMember> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/role`,
    authHeaders,
    { method: "PATCH", body: JSON.stringify({ role }) },
  );
  return jsonOrThrow(r, "change the role");
}

export async function removeOrgMember(
  authHeaders: AuthHeaders,
  orgId: string,
  userId: string,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
    authHeaders,
    { method: "DELETE" },
  );
  await okOrThrow(r, "remove the member");
}

export async function getOrgCreditsActivity(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<CreditsActivity> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/credits/activity`,
    authHeaders,
  );
  if (r.status === 401) throw new Unauthorized();
  return jsonOrThrow(r, "load credit activity");
}

export async function startOrgCreditCheckout(
  authHeaders: AuthHeaders,
  orgId: string,
  amountUsd: number,
): Promise<string> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/credits/checkout`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ amount: String(amountUsd) }) },
  );
  await okOrThrow(r, "start checkout");
  return ((await r.json()) as { url: string }).url;
}

export async function getOrgOverview(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgOverview> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/overview`,
    authHeaders,
  );
  return jsonOrThrow(r, "load the overview");
}

export async function getOrgTraffic(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgTraffic> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/traffic`,
    authHeaders,
  );
  return jsonOrThrow(r, "load the traffic split");
}

export async function setOrgTraffic(
  authHeaders: AuthHeaders,
  orgId: string,
  splitRatio: number,
): Promise<OrgTraffic> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/traffic`,
    authHeaders,
    { method: "PUT", body: JSON.stringify({ split_ratio: splitRatio }) },
  );
  return jsonOrThrow(r, "save the traffic split");
}

export async function setOrgCap(
  authHeaders: AuthHeaders,
  orgId: string,
  body: SpendCapUpdate,
): Promise<OrgOverview> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/cap`,
    authHeaders,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow(r, "save the limit");
}

export async function setOrgPolicy(
  authHeaders: AuthHeaders,
  orgId: string,
  key: string,
  value: boolean,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/settings/${encodeURIComponent(key)}`,
    authHeaders,
    {
      method: "POST",
      body: JSON.stringify({ value: value ? "on" : "off", locked: false }),
    },
  );
  await okOrThrow(r, "change this setting");
}

export async function getOrgDailySavings(
  authHeaders: AuthHeaders,
  orgId: string,
  days = 30,
): Promise<DailyPoint[]> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/savings/daily?days=${days}`,
    authHeaders,
  );
  await okOrThrow(r, "load savings");
  const body = (await r.json()) as { days: DailyPoint[] };
  return body.days || [];
}

export async function inviteOrgMember(
  authHeaders: AuthHeaders,
  orgId: string,
  email: string,
  role: string,
  seatTier?: SeatTier | null,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/invite`,
    authHeaders,
    {
      method: "POST",
      body: JSON.stringify({ email, role, seat_tier: seatTier ?? null }),
    },
  );
  if (r.status === 409) throw new Error("already a member or invited");
  await okOrThrow(r, "send the invitation");
}

export async function exportOrgSpend(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<Blob> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/export`,
    authHeaders,
  );
  await okOrThrow(r, "download the CSV");
  return await r.blob();
}

export async function setCommsProduct(
  authHeaders: AuthHeaders,
  subscribed: boolean,
): Promise<void> {
  const r = await authedFetch("/v1/me/comms/product", authHeaders, {
    method: "POST",
    body: JSON.stringify({ subscribed }),
  });
  await okOrThrow(r, "change your email preference");
}

export async function getCreditsActivity(
  authHeaders: AuthHeaders,
): Promise<CreditsActivity> {
  const r = await authedFetch("/v1/me/credits/activity", authHeaders);
  if (r.status === 401) throw new Unauthorized();
  return jsonOrThrow(r, "load credit activity");
}

export async function startCreditCheckout(
  authHeaders: AuthHeaders,
  amountUsd: number,
): Promise<string> {
  const r = await authedFetch("/v1/me/billing/credits/checkout", authHeaders, {
    method: "POST",
    body: JSON.stringify({ amount_usd: String(amountUsd) }),
  });
  if (r.status === 401) throw new Unauthorized();
  await okOrThrow(r, "start checkout");
  return ((await r.json()) as { url: string }).url;
}

export async function getOrgBilling(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgBilling> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/billing`,
    authHeaders,
  );
  return jsonOrThrow(r, "load billing");
}

// Raw Response so the billing tab can branch on 409 (already subscribed) and
// 503 (billing unconfigured) before reading the redirect url.
export async function startOrgBillingCheckout(
  authHeaders: AuthHeaders,
  orgId: string,
  seats: Record<string, number>,
): Promise<Response> {
  return authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/billing/checkout`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ seats }) },
  );
}

export async function openOrgBillingPortal(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<string> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/billing/portal`,
    authHeaders,
    { method: "POST" },
  );
  await okOrThrow(r, "open the billing portal");
  return ((await r.json()) as { url: string }).url;
}

export async function setOrgSeats(
  authHeaders: AuthHeaders,
  orgId: string,
  seats: Record<string, number>,
): Promise<OrgBilling> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/billing/seats`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ seats }) },
  );
  return jsonOrThrow(r, "change seats");
}

// --- Settings (org profile / billing extras / security / defaults / notifs) ---

export async function getOrgSettings(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgSettings> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/settings`,
    authHeaders,
  );
  return jsonOrThrow(r, "load settings");
}

// Store one org setting. value=null clears it. locked hides it from members.
export async function setOrgSetting(
  authHeaders: AuthHeaders,
  orgId: string,
  key: string,
  value: string | null,
  locked = false,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/settings/${encodeURIComponent(key)}`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ value, locked }) },
  );
  await okOrThrow(r, "save the setting");
}

export async function updateOrgProfile(
  authHeaders: AuthHeaders,
  orgId: string,
  body: {
    name?: string;
    address?: string;
    country?: string;
    tax_id?: string;
  },
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}`,
    authHeaders,
    { method: "PATCH", body: JSON.stringify(body) },
  );
  await okOrThrow(r, "save the organization details");
}

export async function deleteOrg(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}`,
    authHeaders,
    { method: "DELETE" },
  );
  await okOrThrow(r, "delete the organization");
}

// --- Invitations ---

export async function listOrgInvitations(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgInvitation[]> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/invitations`,
    authHeaders,
  );
  return jsonOrThrow(r, "load invitations");
}

export async function revokeOrgInvitation(
  authHeaders: AuthHeaders,
  orgId: string,
  invitationId: string,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}`,
    authHeaders,
    { method: "DELETE" },
  );
  await okOrThrow(r, "revoke the invitation");
}

export async function getOrgModels(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgModels> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/models`,
    authHeaders,
  );
  return jsonOrThrow(r, "load models");
}

// --- Org-scoped API keys ---

export async function listOrgKeys(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgKey[]> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/keys`,
    authHeaders,
  );
  return jsonOrThrow(r, "load API keys");
}

export async function createOrgKey(
  authHeaders: AuthHeaders,
  orgId: string,
  name?: string,
  expiresInDays?: number,
  controls: { access?: KeyAccess; rpm_limit?: number | null } = {},
): Promise<OrgKeyMinted> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/keys`,
    authHeaders,
    {
      method: "POST",
      body: JSON.stringify({
        name: name ?? null,
        expires_in_days: expiresInDays ?? null,
        ...controls,
      }),
    },
  );
  return jsonOrThrow(r, "create the key");
}

export async function updateOrgKey(
  authHeaders: AuthHeaders,
  orgId: string,
  keyId: string,
  patch: { name?: string; access?: KeyAccess; rpm_limit?: number | null },
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/keys/${encodeURIComponent(keyId)}`,
    authHeaders,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
  await okOrThrow(r, "update the key");
}

export async function rollOrgKey(
  authHeaders: AuthHeaders,
  orgId: string,
  keyId: string,
): Promise<OrgKeyMinted> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/keys/${encodeURIComponent(keyId)}/roll`,
    authHeaders,
    { method: "POST" },
  );
  return jsonOrThrow(r, "roll the key");
}

export async function revokeOrgKey(
  authHeaders: AuthHeaders,
  orgId: string,
  keyId: string,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/keys/${encodeURIComponent(keyId)}`,
    authHeaders,
    { method: "DELETE" },
  );
  await okOrThrow(r, "revoke the key");
}

// ── Observability ───────────────────────────────────────────────────────────
// `base` is `/v1/orgs/{id}/analytics` (admin) or `/v1/me/analytics`.

export function orgInsightsBase(orgId: string): string {
  return `/v1/orgs/${encodeURIComponent(orgId)}/analytics`;
}

export const ME_INSIGHTS_BASE = "/v1/me/analytics";

function insightParams(
  range: DateRange,
  filters: InsightFilters,
  extra: Record<string, string> = {},
): string {
  const p = new URLSearchParams({
    start: range.start,
    end: range.end,
    ...extra,
  });
  for (const [k, v] of Object.entries(filters)) if (v) p.set(k, v);
  return p.toString();
}

export async function getBreakdown(
  authHeaders: AuthHeaders,
  base: string,
  range: DateRange,
  groupBy: InsightDimension,
  filters: InsightFilters = {},
  metric: SeriesMetric = "cost_usd",
  priceBreakdown = true,
): Promise<Breakdown> {
  const q = insightParams(range, filters, {
    group_by: groupBy,
    metric,
    price_breakdown: String(priceBreakdown),
  });
  const r = await authedFetch(`${base}/breakdown?${q}`, authHeaders);
  return jsonOrThrow(r, "load this data");
}

export async function getFlow(
  authHeaders: AuthHeaders,
  base: string,
  range: DateRange,
  by: FlowBy,
  measure: FlowMeasure,
  filters: InsightFilters = {},
): Promise<Flow> {
  const q = insightParams(range, filters, {
    by,
    measure,
    price_breakdown: "true",
  });
  const r = await authedFetch(`${base}/flow?${q}`, authHeaders);
  return jsonOrThrow(r, "load the flow");
}

export async function getUseCases(
  authHeaders: AuthHeaders,
  base: string,
  range: DateRange,
  axis: UseCaseAxis,
  filters: InsightFilters = {},
): Promise<UseCases> {
  const q = insightParams(range, filters, { axis });
  const r = await authedFetch(`${base}/use-cases?${q}`, authHeaders);
  return jsonOrThrow(r, "load use cases");
}

export async function getUsageValue(
  authHeaders: AuthHeaders,
  range: DateRange,
): Promise<UsageValue> {
  const q = insightParams(range, {}, { price_breakdown: "true" });
  const r = await authedFetch(`${ME_INSIGHTS_BASE}/value?${q}`, authHeaders);
  return jsonOrThrow(r, "load your usage value");
}

export async function getToolsBreakdown(
  authHeaders: AuthHeaders,
  base: string,
  range: DateRange,
  groupBy: ToolDimension,
  filters: InsightFilters = {},
): Promise<ToolsBreakdown> {
  const q = insightParams(range, filters, { group_by: groupBy });
  const r = await authedFetch(`${base}/tools?${q}`, authHeaders);
  return jsonOrThrow(r, "load tools");
}

export async function getActivitySessions(
  authHeaders: AuthHeaders,
  base: string,
  range: DateRange,
  filters: InsightFilters = {},
): Promise<ActivitySession[]> {
  return (await getActivitySessionPage(authHeaders, base, range, filters))
    .sessions;
}

/** The latest sessions in range (capped) and how many there are in all. */
export async function getActivitySessionPage(
  authHeaders: AuthHeaders,
  base: string,
  range: DateRange,
  filters: InsightFilters = {},
): Promise<{ sessions: ActivitySession[]; total: number | null }> {
  const r = await authedFetch(
    `${base}/sessions?${insightParams(range, filters)}`,
    authHeaders,
  );
  await okOrThrow(r, "load sessions");
  const body = (await r.json()) as {
    sessions: ActivitySession[];
    total?: number;
  };
  return { sessions: body.sessions, total: body.total ?? null };
}

export async function getSessionTimeline(
  authHeaders: AuthHeaders,
  base: string,
  sessionId: string,
): Promise<TimelineRequest[]> {
  const r = await authedFetch(
    `${base}/sessions/${encodeURIComponent(sessionId)}`,
    authHeaders,
  );
  await okOrThrow(r, "load the timeline");
  return ((await r.json()) as { requests: TimelineRequest[] }).requests;
}

async function downloadCsv(
  authHeaders: AuthHeaders,
  base: string,
  range: DateRange,
  groupBy: InsightDimension,
  filters: InsightFilters,
): Promise<void> {
  const q = insightParams(range, filters, {
    group_by: groupBy,
    price_breakdown: "true",
  });
  const r = await authedFetch(`${base}/export?${q}`, authHeaders);
  await okOrThrow(r, "download the CSV");
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `condense-${groupBy}-${range.start}-${range.end}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadBreakdownCsv(
  authHeaders: AuthHeaders,
  orgId: string,
  range: DateRange,
  groupBy: InsightDimension,
  filters: InsightFilters = {},
): Promise<void> {
  return downloadCsv(
    authHeaders,
    orgInsightsBase(orgId),
    range,
    groupBy,
    filters,
  );
}

export function downloadMyBreakdownCsv(
  authHeaders: AuthHeaders,
  range: DateRange,
  groupBy: InsightDimension,
  filters: InsightFilters = {},
): Promise<void> {
  return downloadCsv(authHeaders, ME_INSIGHTS_BASE, range, groupBy, filters);
}

/** Lines past which condense emails you: one session's list-price cost, and
 * this month's billed spend. Dollar strings; null when unset. */
export type MyAlerts = { session_usd: string | null; month_usd: string | null };

export async function getMyAlerts(authHeaders: AuthHeaders): Promise<MyAlerts> {
  const r = await authedFetch("/v1/me/alerts", authHeaders);
  return jsonOrThrow(r, "load your alerts");
}

export async function setMyAlerts(
  authHeaders: AuthHeaders,
  patch: Partial<MyAlerts>,
): Promise<MyAlerts> {
  const r = await authedFetch("/v1/me/alerts", authHeaders, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (r.status === 422) throw new Error("Enter a positive amount.");
  return jsonOrThrow(r, "save your alerts");
}

export async function listOrgTeams(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgTeam[]> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/teams`,
    authHeaders,
  );
  return jsonOrThrow(r, "load teams");
}

export async function saveOrgTeam(
  authHeaders: AuthHeaders,
  orgId: string,
  team: {
    id?: string;
    name: string;
    member_ids: string[];
    cost_center?: string | null;
  },
): Promise<OrgTeam> {
  const base = `/v1/orgs/${encodeURIComponent(orgId)}/teams`;
  const r = await authedFetch(
    team.id ? `${base}/${encodeURIComponent(team.id)}` : base,
    authHeaders,
    {
      method: team.id ? "PATCH" : "POST",
      body: JSON.stringify({
        name: team.name,
        member_ids: team.member_ids,
        cost_center: team.cost_center ?? "",
      }),
    },
  );
  return jsonOrThrow(r, "save the team");
}

export async function deleteOrgTeam(
  authHeaders: AuthHeaders,
  orgId: string,
  teamId: string,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/teams/${encodeURIComponent(teamId)}`,
    authHeaders,
    { method: "DELETE" },
  );
  await okOrThrow(r, "delete the team");
}

// ── Spend budgets ───────────────────────────────────────────────────────────

function budgetsBase(orgId: string): string {
  return `/v1/orgs/${encodeURIComponent(orgId)}/budgets`;
}

export async function listBudgets(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<SpendBudget[]> {
  const r = await authedFetch(budgetsBase(orgId), authHeaders);
  return jsonOrThrow(r, "load budgets");
}

export async function saveBudget(
  authHeaders: AuthHeaders,
  orgId: string,
  budget: BudgetInput,
  id?: string,
): Promise<SpendBudget> {
  const r = await authedFetch(
    id ? `${budgetsBase(orgId)}/${encodeURIComponent(id)}` : budgetsBase(orgId),
    authHeaders,
    { method: id ? "PUT" : "POST", body: JSON.stringify(budget) },
  );
  return jsonOrThrow(r, "save the budget");
}

export async function deleteBudget(
  authHeaders: AuthHeaders,
  orgId: string,
  id: string,
): Promise<void> {
  const r = await authedFetch(
    `${budgetsBase(orgId)}/${encodeURIComponent(id)}`,
    authHeaders,
    { method: "DELETE" },
  );
  await okOrThrow(r, "delete the budget");
}

export async function listRaiseRequests(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<RaiseRequest[]> {
  const r = await authedFetch(`${budgetsBase(orgId)}/requests`, authHeaders);
  return jsonOrThrow(r, "load raise requests");
}

export async function decideRaiseRequest(
  authHeaders: AuthHeaders,
  orgId: string,
  id: string,
  approve: boolean,
  extraUsd?: string,
): Promise<void> {
  const r = await authedFetch(
    `${budgetsBase(orgId)}/requests/${encodeURIComponent(id)}`,
    authHeaders,
    {
      method: "POST",
      body: JSON.stringify({ approve, extra_usd: extraUsd ?? null }),
    },
  );
  await okOrThrow(r, "save the decision");
}

export async function getChargeback(
  authHeaders: AuthHeaders,
  orgId: string,
  month: string,
): Promise<Chargeback> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/chargeback?month=${month}`,
    authHeaders,
  );
  return jsonOrThrow(r, "load the chargeback");
}

export async function downloadChargeback(
  authHeaders: AuthHeaders,
  orgId: string,
  month: string,
): Promise<void> {
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/chargeback/export?month=${month}`,
    authHeaders,
  );
  await okOrThrow(r, "download the CSV");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(await r.blob());
  a.download = `condense-chargeback-${month}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function getOrgAudit(
  authHeaders: AuthHeaders,
  orgId: string,
  opts: { before?: number; action?: string } = {},
): Promise<AuditPage> {
  const q = new URLSearchParams();
  if (opts.before) q.set("before", String(opts.before));
  if (opts.action) q.set("action", opts.action);
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/audit?${q}`,
    authHeaders,
  );
  return jsonOrThrow(r, "load the audit log");
}

export async function downloadOrgAudit(
  authHeaders: AuthHeaders,
  orgId: string,
  action?: string,
): Promise<void> {
  const q = action ? `?action=${encodeURIComponent(action)}` : "";
  const r = await authedFetch(
    `/v1/orgs/${encodeURIComponent(orgId)}/audit/export${q}`,
    authHeaders,
  );
  await okOrThrow(r, "download the CSV");
  const blob = await r.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "condense-audit.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function getBudgetsOverview(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<BudgetsOverview> {
  const r = await authedFetch(`${budgetsBase(orgId)}/overview`, authHeaders);
  return jsonOrThrow(r, "load budgets");
}

export async function getBudgetSlack(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<boolean> {
  const r = await authedFetch(`${budgetsBase(orgId)}/slack`, authHeaders);
  return (await jsonOrThrow<{ configured: boolean }>(r, "load Slack settings"))
    .configured;
}

export async function setBudgetSlack(
  authHeaders: AuthHeaders,
  orgId: string,
  url: string | null,
): Promise<boolean> {
  const r = await authedFetch(`${budgetsBase(orgId)}/slack`, authHeaders, {
    method: "PUT",
    body: JSON.stringify({ url }),
  });
  return (await jsonOrThrow<{ configured: boolean }>(r, "save Slack settings"))
    .configured;
}

export async function getSessionAlert(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<string | null> {
  const r = await authedFetch(
    `${budgetsBase(orgId)}/session-alert`,
    authHeaders,
  );
  return (
    await jsonOrThrow<{ usd: string | null }>(r, "load the session alert")
  ).usd;
}

export async function setSessionAlert(
  authHeaders: AuthHeaders,
  orgId: string,
  usd: string | null,
): Promise<string | null> {
  const r = await authedFetch(
    `${budgetsBase(orgId)}/session-alert`,
    authHeaders,
    {
      method: "PUT",
      body: JSON.stringify({ usd }),
    },
  );
  return (
    await jsonOrThrow<{ usd: string | null }>(r, "save the session alert")
  ).usd;
}

export async function listMyBudgets(
  authHeaders: AuthHeaders,
): Promise<SpendBudget[]> {
  const r = await authedFetch("/v1/me/budgets", authHeaders);
  return jsonOrThrow(r, "load your budgets");
}

export async function getMyBudgetDrivers(
  authHeaders: AuthHeaders,
  budgetId: string,
  groupBy: "model" | "repo",
): Promise<Breakdown> {
  const r = await authedFetch(
    `/v1/me/budgets/${encodeURIComponent(budgetId)}/drivers?group_by=${groupBy}`,
    authHeaders,
  );
  return jsonOrThrow(r, "load this breakdown");
}

export async function askForRaise(
  authHeaders: AuthHeaders,
  budgetId: string,
  note: string | null,
): Promise<void> {
  const r = await authedFetch(
    `/v1/me/budgets/${encodeURIComponent(budgetId)}/raise`,
    authHeaders,
    { method: "POST", body: JSON.stringify({ note }) },
  );
  await okOrThrow(r, "send your request");
}

// --- Outbound webhooks ---

function hooksBase(orgId: string): string {
  return `/v1/orgs/${encodeURIComponent(orgId)}/webhooks`;
}

export async function listWebhooks(
  authHeaders: AuthHeaders,
  orgId: string,
): Promise<OrgWebhook[]> {
  const r = await authedFetch(hooksBase(orgId), authHeaders);
  return jsonOrThrow(r, "load webhooks");
}

export async function createWebhook(
  authHeaders: AuthHeaders,
  orgId: string,
  url: string,
  events: string[],
): Promise<OrgWebhookMinted> {
  const r = await authedFetch(hooksBase(orgId), authHeaders, {
    method: "POST",
    body: JSON.stringify({ url, events }),
  });
  return jsonOrThrow(r, "add the endpoint");
}

export async function deleteWebhook(
  authHeaders: AuthHeaders,
  orgId: string,
  id: string,
): Promise<void> {
  const r = await authedFetch(
    `${hooksBase(orgId)}/${encodeURIComponent(id)}`,
    authHeaders,
    { method: "DELETE" },
  );
  await okOrThrow(r, "remove the endpoint");
}

export async function testWebhook(
  authHeaders: AuthHeaders,
  orgId: string,
  id: string,
): Promise<WebhookDelivery> {
  const r = await authedFetch(
    `${hooksBase(orgId)}/${encodeURIComponent(id)}/test`,
    authHeaders,
    { method: "POST" },
  );
  return jsonOrThrow(r, "send the test event");
}

export async function listWebhookDeliveries(
  authHeaders: AuthHeaders,
  orgId: string,
  id: string,
): Promise<WebhookDelivery[]> {
  const r = await authedFetch(
    `${hooksBase(orgId)}/${encodeURIComponent(id)}/deliveries`,
    authHeaders,
  );
  return jsonOrThrow(r, "load deliveries");
}

export async function retryWebhookDelivery(
  authHeaders: AuthHeaders,
  orgId: string,
  id: string,
  deliveryId: number,
): Promise<void> {
  const r = await authedFetch(
    `${hooksBase(orgId)}/${encodeURIComponent(id)}/deliveries/${deliveryId}/retry`,
    authHeaders,
    { method: "POST" },
  );
  await okOrThrow(r, "retry the delivery");
}
